import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import i18n from '../../../i18n';

import { useSilentRegistration } from 'core/silentRegistration/silentRegistration';
import { useAction } from 'core/store/store';

import {
    makeOnboardingBackAction,
    makeOnboardingFinishAction,
    makeOnboardingSubmitSellerFormRequestAction,
    makeOnboardingSaveSellerFormDataAction,
    makeOnboardingSaveStoreFormDataAction,
} from 'state/onboarding/actions';
import { onboardingScreenSelector } from 'state/onboarding/selectors';

import { LayoutGrowItem } from 'components/Layout/LayoutGrowItem';
import { LayoutFullHeightContainer } from 'components/Layout/LayoutFullHeightContainer';
import { LayoutFixedItem } from 'components/Layout/LayoutFixedItem';
import { SilentLoginOtpForm } from '../Checkout/SilentLoginOtpForm';

import { RoutePath } from 'RoutePath';

import { CroppedArea, UserRole } from 'types';

import { Header } from './Header';
import { Catalog } from './Catalog';
import { Seller } from './Seller';
import { Store } from './Store';

import { OnboardingScreen, SellerFormData, StoreFormData } from './types';
import { correlationIdSelector } from 'state/signup/selectors';
import { SilentLoginPasswordForm } from 'pages/Checkout/SilentLoginPasswordForm';

import css from './Onboarding.module.css';
import hotkeys from 'hotkeys-js';
import { logout } from 'core/auth/auth';

interface OnboardingContentProps {
    currentStep: OnboardingScreen;
    onSellerFormSubmit: (data: SellerFormData) => void;
    onStoreFormSubmit: (data: StoreFormData) => void;
    onStoreFormImageCrop: (crop: CroppedArea) => void;
    onCatalogFinish: () => void;
}

export const OnboardingContent = (props: OnboardingContentProps) => {
    switch (props.currentStep) {
        case OnboardingScreen.SELLER:
            return <Seller onSubmit={props.onSellerFormSubmit} />;
        case OnboardingScreen.STORE:
            return (
                <Store
                    onImageCrop={props.onStoreFormImageCrop}
                    onSubmit={props.onStoreFormSubmit}
                    isCreateMode={true}
                />
            );
        case OnboardingScreen.CATALOG:
            return <Catalog isOnboarding={true} onFinish={props.onCatalogFinish} />;
        default:
            return null;
    }
};

export const Onboarding = () => {
    const screen = useSelector(onboardingScreenSelector);
    // const classes = useStyles({ screen });

    const action = useAction();

    const { t } = i18n.useTranslation();

    const [userAlreadyExist, setUserAlreadyExist] = useState(false);

    const correlationId = useSelector(correlationIdSelector);

    const handleSilentRegistrationSuccess = useCallback(
        (jwt: string, phoneNumber: string, userAlreadyExists?: boolean) => {
            action(makeOnboardingSubmitSellerFormRequestAction, jwt, phoneNumber, userAlreadyExists);
        },
        [action],
    );

    const silentRegistration = useSilentRegistration(UserRole.VENDOR, handleSilentRegistrationSuccess, correlationId);

    const handleSellerFormSubmit = useCallback(
        async (data: SellerFormData) => {
            const { phone, captcha } = data;
            silentRegistration.setPhoneNumber(phone);
            const generateAnswer = await silentRegistration.generateOtp(phone, captcha);
            if (generateAnswer === 'userAlreadyExists') {
                setUserAlreadyExist(true);
            } else {
                action(makeOnboardingSaveSellerFormDataAction, data);
            }
        },
        [silentRegistration.setPhoneNumber, silentRegistration.generateOtp, action],
    );

    const handleOtpFormSubmit = useCallback(
        (otp: string) => {
            return silentRegistration.verifyOtp(silentRegistration.phoneNumber, otp);
        },
        [silentRegistration.verifyOtp, silentRegistration.phoneNumber, action],
    );

    const handleResend = useCallback(async () => {
        await silentRegistration.generateOtp(silentRegistration.phoneNumber);
    }, [silentRegistration.generateOtp, silentRegistration.phoneNumber]);

    const [storeFormImageCrop, setStoreFormImageCrop] = useState<CroppedArea>();

    const handleStoreFormSubmit = useCallback(
        async (data: StoreFormData) => {
            action(makeOnboardingSaveStoreFormDataAction, { ...data, imageCrop: storeFormImageCrop }, true);
        },
        [storeFormImageCrop, action],
    );

    const handleFinish = useCallback(() => {
        action(makeOnboardingFinishAction);
    }, [action]);

    const handleBack = useCallback(() => {
        action(makeOnboardingBackAction);
    }, [action]);

    const sellerPhoneError =
        silentRegistration.errorMessage ??
        (silentRegistration.shouldTryAgainLater && t('common:phoneVerificationControl.tryAgainLater'));

    const handleCloseLoginForm = useCallback(() => setUserAlreadyExist(false), [setUserAlreadyExist]);

    const handleLogin = useCallback(
        (password: string) => silentRegistration.login(silentRegistration.phoneNumber, password),
        [silentRegistration],
    );

    useEffect(() => {
        const HOTKEY = 'ctrl+alt+l,cmd+alt+l';

        hotkeys(HOTKEY, (event) => {
            event.preventDefault();
            logout(RoutePath.LOGIN);
        });
        return () => {
            hotkeys.unbind(HOTKEY)
        }
    }, [])

    if (userAlreadyExist) {
        return (
            <SilentLoginPasswordForm
                errorMessage={sellerPhoneError}
                onClose={handleCloseLoginForm}
                onBack={handleCloseLoginForm}
                onNext={handleLogin}
            />
        );
    }

    if (screen === OnboardingScreen.SUCCESS) {
        return <Redirect to={RoutePath.VENDOR_DASHBOARD} />;
    }

    if (screen === OnboardingScreen.SELLER_PHONE_VERIFICATION) {
        return (
            <SilentLoginOtpForm
                errorMessage={sellerPhoneError}
                isDisabled={silentRegistration.isPending}
                phoneNumber={silentRegistration.phoneNumber}
                timer={silentRegistration.timer}
                onBack={handleBack}
                onNext={handleOtpFormSubmit}
                onResend={handleResend}
                setErrorMessage={silentRegistration.setErrorMessage}
            />
        );
    }

    return (
        <LayoutFullHeightContainer>
            <LayoutFixedItem className={clsx(css.header, screen === OnboardingScreen.CATALOG && css.headerCatalog)}>
                <Header currentScreen={screen} />
            </LayoutFixedItem>

            <LayoutGrowItem className={clsx(css.content, screen === OnboardingScreen.CATALOG && css.contentCatalog)}>
                <OnboardingContent
                    currentStep={screen}
                    onSellerFormSubmit={handleSellerFormSubmit}
                    onStoreFormSubmit={handleStoreFormSubmit}
                    onStoreFormImageCrop={setStoreFormImageCrop}
                    onCatalogFinish={handleFinish}
                />
            </LayoutGrowItem>
        </LayoutFullHeightContainer>
    );
};
